import { useState, useMemo, useEffect, type ChangeEvent, type MouseEvent } from "react";
import { humanServerError } from "../lib/card-actions";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, ApiError, type Card, type CardTypeFilter, type AuthUser } from "../lib/api";
import { aggregateConfidence, bucketOf, cardMaxTier } from "../lib/confidence";
import { isZulagenblattData } from "../lib/zulagen-render";
import { isFahrtenbuchData } from "../lib/docTypeView";
import { deriveFilterTabs } from "../lib/cardFilterTabs";
import { TierBadge } from "../components/TierBadge";
import { useKanbanPoll } from "../lib/useKanbanPoll";
import { useCardsCounts } from "../lib/useCardsCounts";
import { useAttentionPoll } from "../lib/useAttentionPoll";
import { useClientContext } from "../lib/ClientContext";
import { useSelectedMonth } from "../lib/useSelectedMonth";
import { useViewport } from "../lib/useViewport";
import { computeOcrProgress, useQueueAvgRuntime, formatElapsed } from "../lib/ocrProgress";
import { UploadProgressBanner } from "../components/UploadProgressBanner";

const ACTIVE_UPLOADS_LS_KEY = "sibit.activeUploads";

interface ActiveUpload {
  id: string;
  filename: string;
}

function loadActiveUploads(): ActiveUpload[] {
  try {
    const raw = localStorage.getItem(ACTIVE_UPLOADS_LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x) => x && typeof x.id === "string" && typeof x.filename === "string");
  } catch { return []; }
}

function saveActiveUploads(uploads: ActiveUpload[]): void {
  try { localStorage.setItem(ACTIVE_UPLOADS_LS_KEY, JSON.stringify(uploads)); }
  catch { /* localStorage full or unavailable */ }
}

// 2026-05-19: 'uploaded' + 'ocr_pending' + 'ocr_running' zusammengefasst in
// eine Spalte "OCR" — die drei states sind aus User-Sicht "im Pre-OCR-Flow",
// nichts zu tun für Birgit/Thomas. Platz für "Exportiert" wieder frei.
const COLUMNS: { state: string; label: string }[] = [
  { state: "ocr_pending", label: "OCR" },
  // pending_review: OCR-Pipeline hat die Karte nicht eindeutig zuordnen
  // können (Mitarbeiter-Match fehl, Rotation unklar, OCR-Quality zu schwach).
  { state: "pending_review", label: "Prüfen" },
  { state: "ocr_complete", label: "OCR fertig" },
  { state: "validated", label: "Validiert" },
  { state: "calculated", label: "Berechnet" },
  { state: "exported", label: "Exportiert" },
  // Karte 589: Sonstige = AUFFANGBECKEN (Birgit-Call 21.07.), kein
  // Prozess-Schritt — nicht-zuordenbare Seiten ruhen hier sichtbar
  // (nichts verschwindet, #523) statt die Prüfen-Spalte zu fluten.
  // Ausgänge: Deklaration (#563) oder Archiv.
  { state: "sonstige", label: "Sonstige" },
];

// card #523: die Filter-Tabs sind NICHT mehr ein fixes Enum. Sie werden pro
// Mandant dynamisch aus den Upload-Doc-Typen + den auf Karten vorkommenden
// doc_types abgeleitet (lib/cardFilterTabs.ts). Kein hardcodiertes TYPE_TABS.

function cardLink(c: Card): string {
  // Conflict cards route to the comparison page regardless of kind
  if (c.state === "pending_conflict") {
    return `/conflicts/${c.kind === "timesheet" ? "timesheet" : "expense"}/${c.id}`;
  }
  return c.kind === "timesheet" ? `/timesheets/${c.id}` : `/expenses/${c.id}`;
}

/** Open the Pipeline-Werkstatt for a card, deep-linked to the most relevant phase
 *  (Glass-Box-Pattern: "Step gehört wo er läuft" — phase by current state). */
function werkstattLink(c: Card): string {
  const s = c.state;
  // archived/superseded: Karte beendet. Default-Tab "upload" zeigt die
  // Pipeline-Übersicht (pipelineFlow.ts behandelt TERMINAL als end-state für
  // alle Phasen) — der Owner sieht Status + Migration-/Archive-History.
  if (s === "archived" || s === "superseded") return `/werkstatt/abrechnung/upload?card=${c.id}`;
  if (s === "uploaded" || s === "pending" || s === "pending_review" || s === "sonstige") return `/werkstatt/abrechnung/upload?card=${c.id}`;
  if (s === "pending_conflict") return `/werkstatt/abrechnung/konflikt?card=${c.id}`;
  if (s === "validated") return `/werkstatt/abrechnung/validierung?card=${c.id}`;
  if (s === "calculated") return `/werkstatt/abrechnung/berechnung?card=${c.id}`;
  if (s === "exported") return `/werkstatt/abrechnung/export?card=${c.id}`;
  return `/werkstatt/abrechnung/ocr?card=${c.id}`;
}

function kindIcon(kind: Card["kind"]): string {
  if (kind === "timesheet") return "🕐";
  if (kind === "expense") return "🧳";
  if (kind === "excel_import") return "";   // T13: kein Emoji — Text-Badge reicht
  return "❓";
}

function kindLabel(kind: Card["kind"]): string {
  if (kind === "timesheet") return "Std";   // kompakt; voller MA-Name steht im Gruppenkopf
  if (kind === "expense") return "Diät";
  if (kind === "excel_import") return "Excel-Import";
  return "?";
}

interface UploadSummary {
  filename: string;
  /** Quelle — steuert die Labels (PDF: Seiten/Stunden/Diäten; Excel:
   *  Zeilen/Karten, Zulagen/Reisekosten). Default 'pdf'. */
  source?: "pdf" | "excel";
  page_count_in: number; // PDF: Seiten; Excel: importierte Zeilen
  timesheets: number; // PDF: Stunden-Karten; Excel: Zulagen-Karten (zulagenblatt)
  expenses: number; // PDF: Diäten-Karten; Excel: Reisekosten-Karten (fahrtenbuch)
  unknown: number;
  blank: number;
  // Excel-only:
  karten_count?: number;
  employees_matched?: number;
}

/** Excel-Upload (.xlsx) vs PDF — steuert Endpoint-Routing + doc_type-Selektor
 *  (Excel wird pro Datensatz über die Art typisiert, ein Datei-doc_type wäre
 *  bedeutungslos). Entscheidung per Dateiendung — KEIN Mandanten-Hardcode. */
const isXlsxFile = (f: File): boolean => f.name.toLowerCase().endsWith(".xlsx");

interface DuplicateDialog {
  filename: string;
  earlier_upload: {
    uploaded_at: string;
    filename: string;
    by_user: string;
    cards: Array<{ id: string; kind: string; month: string; state: string; employee_id: string | null }>;
  };
}

interface MergeInfo {
  merged_card_id: string;
  target_card_id: string;
  month: string;
  first_name: string | null;
  last_name: string | null;
}

export function KanbanPage() {
  const { month, setMonth } = useSelectedMonth();
  const location = useLocation();
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState<CardTypeFilter>("all");
  // Archive-Sektion (unter den 6 normalen Kanban-Spalten): default zugeklappt,
  // separater Polling-Call mit archived_only=true. Glass-Box-Lücke geschlossen
  // (2026-05-21): vor diesem Patch hatten state='archived'/'superseded' Karten
  // KEINEN UI-Frame — sie verschwanden komplett aus der Wahrnehmung. Anlass:
  // Mesaljic Sanela + Omer Osmankic waren als archived gelandet und nirgendwo
  // sichtbar. Pattern „Glass-Box: jedes Atom braucht eine UI-Adresse".
  const [archiveExpanded, setArchiveExpanded] = useState(false);
  const {
    clients,
    loaded: clientsLoaded,
    selectedClientId: clientId,
    setSelectedClientId: setClientId,
  } = useClientContext();
  const { cards, loading, error, refresh } = useKanbanPoll(month, clientId, typeFilter);
  // Lazy-Load: nur pollen wenn aufgeklappt — sonst null Bandbreite/DB-Last.
  const { cards: archivedCards } = useKanbanPoll(
    month, clientId, typeFilter,
    { archivedOnly: true, enabled: archiveExpanded },
  );
  // Reused for the board's empty-state: same months_with_work payload the inbox
  // already serves. Inbox shows "open attention" — same definition fits here too;
  // a month with no cards at all certainly has no open attention.
  const { data: attention } = useAttentionPoll(month, clientId);
  const [uploading, setUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [uploadSummaries, setUploadSummaries] = useState<UploadSummary[]>([]);
  const [duplicateDialog, setDuplicateDialog] = useState<DuplicateDialog | null>(null);
  const [mergeBanner, setMergeBanner] = useState<MergeInfo[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mergeMode, setMergeMode] = useState<"auto_with_banner" | "confirm">("auto_with_banner");
  const [processMode, setProcessMode] = useState<"autonomous" | "manual">("autonomous");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const [exportStatus, setExportStatus] = useState<{ ready: boolean; total: number; ready_for: number } | null>(null);
  // currentUser für Werkstatt-Link-Gating (developer-only). /api/me wird beim
  // App-Start ohnehin gerufen — hier re-fetch um die Page selbständig zu halten.
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  useEffect(() => { api.me().then(setCurrentUser).catch(() => setCurrentUser(null)); }, []);
  const isDeveloper = currentUser?.role === "developer";
  const [dragOver, setDragOver] = useState(false);
  const [activeUploads, setActiveUploads] = useState<ActiveUpload[]>(() => loadActiveUploads());
  // Deterministic per-upload month: pop a confirmation modal so the operator
  // makes an explicit choice rather than letting the toolbar default leak in.
  const [pendingUpload, setPendingUpload] = useState<{ files: File[] } | null>(null);
  const [pendingMonth, setPendingMonth] = useState<string>(month);
  const [pendingDocType, setPendingDocType] = useState<string>("");
  // routesTo als Union (nicht string): die Sonstige-Karte leitet daraus direkt
  // das Umsortier-Ziel ab — ein loses `string` haette genau die Verwechslung
  // wieder moeglich gemacht, die den Anlassfall 2026-08-12 erzeugt hat.
  const [docTypeOptions, setDocTypeOptions] = useState<Array<{ id: string; label: string; routesTo: "timesheet" | "expense" }>>([]);
  // #562-Rework: darf dieser Mandant „Automatisch erkennen"? (Regelwerk-Flag)
  const [uploadAutoDetect, setUploadAutoDetect] = useState(false);

  const trackUpload = (id: string, filename: string) => {
    setActiveUploads((prev) => {
      if (prev.some((u) => u.id === id)) return prev;
      const next = [...prev, { id, filename }];
      saveActiveUploads(next);
      return next;
    });
  };
  const untrackUpload = (id: string) => {
    setActiveUploads((prev) => {
      const next = prev.filter((u) => u.id !== id);
      saveActiveUploads(next);
      return next;
    });
  };

  // Bug 2 (Thomas 24.07.): Quelle-Filter — Karten aus dem Excel-Import
  // (kind='excel_import') von PDF-/Scan-Karten trennen koennen.
  const [quelleFilter, setQuelleFilter] = useState<"alle" | "pdf" | "excel">("alle");

  const byColumn = useMemo(() => {
    const m = new Map<string, Card[]>();
    for (const col of COLUMNS) m.set(col.state, []);
    for (const c of cards) {
      if (quelleFilter === "excel" && c.kind !== "excel_import") continue;
      if (quelleFilter === "pdf" && c.kind === "excel_import") continue;
      // uploaded + ocr_running werden in die "OCR"-Spalte (ocr_pending)
      // gemerged — visuell ein Bucket für alle pre-OCR-fertig Zustände.
      const col = (c.state === "ocr_running" || c.state === "uploaded")
        ? "ocr_pending" : c.state;
      const bucket = m.get(col);
      if (bucket) bucket.push(c);
    }
    return m;
  }, [cards, quelleFilter]);

  // Tab-Badge-Counter aus dem Server-Aggregat. NICHT mehr aus dem bereits
  // gefilterten `cards`-Array zählen — das war der Drift-Bug (2026-05-19,
  // verifiziert auf Monat 2026-04): Tabs außer "Alle" zeigten 0 für nicht-
  // aktive Kategorien, weil die Karten serverseitig schon ausgefiltert
  // waren. Single source: /api/cards/counts via useCardsCounts hook.
  // Konsistenz-Check sichtbar im Werkstatt-Tab "Bestand".
  const typeCounts = useCardsCounts(month, clientId);

  // card #523: Filter-Tabs dynamisch aus den Upload-Doc-Typen des Mandanten
  // (docTypeOptions — dieselbe Quelle wie der Upload-Dialog) ∪ den doc_types,
  // die tatsächlich auf Karten vorkommen (Server-Aggregat by_doc_type) ∪
  // {Unklar, Konflikt}. Kein fixes Enum. Mandant-Isolation ist strukturell:
  // beide Quellen sind schon pro Mandant.
  const filterTabs = useMemo(
    () => deriveFilterTabs(docTypeOptions, Object.keys(typeCounts.by_doc_type)),
    [docTypeOptions, typeCounts.by_doc_type],
  );

  // Wenn der aktive Filter keinen Tab mehr hat (z. B. nach Mandant-Wechsel:
  // ein 3g-doc_type existiert bei Zöscher nicht) → zurück auf "Alle", damit nie
  // ein „unsichtbarer" Filter ohne Tab-Highlight hängen bleibt.
  useEffect(() => {
    if (!filterTabs.some((t) => t.id === typeFilter)) setTypeFilter("all");
  }, [filterTabs, typeFilter]);

  // Load user preferences once on mount
  useEffect(() => {
    api.preferences.get().then((p: any) => {
      if (p.diaet_merge_mode) setMergeMode(p.diaet_merge_mode);
      if (p.process_mode) setProcessMode(p.process_mode);
    }).catch(() => {});
  }, []);

  // Poll export status every 15s
  useEffect(() => {
    const refreshStatus = async () => {
      try {
        const s = await api.export.status(month, clientId);
        setExportStatus({ ready: s.export_ready, total: s.total, ready_for: s.ready_for_export });
      } catch { setExportStatus(null); }
    };
    refreshStatus();
    const t = setInterval(refreshStatus, 15_000);
    return () => clearInterval(t);
  }, [month, clientId]);

  // Poll merge-banner every 10s (24h window, auto_with_banner mode only)
  useEffect(() => {
    if (mergeMode !== "auto_with_banner") { setMergeBanner([]); return; }
    const fetchMerges = async () => {
      try {
        const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const merges = await api.expenseMerges.since(since, clientId);
        setMergeBanner(merges);
      } catch { /* ignore */ }
    };
    fetchMerges();
    const t = setInterval(fetchMerges, 10_000);
    return () => clearInterval(t);
  }, [clientId, mergeMode]);

  /** Send the queued PDFs to the upload endpoint with the operator-chosen
   *  month. Called only after the month-confirmation modal is confirmed. */
  // Per-Mandant Dokumenttypen + Auto-Erkennungs-Flag für den Upload-Selektor laden.
  // #562-Rework: Auto-Option nur, wenn der Mandant sie per Regelwerk aktiviert
  // hat (upload_auto_detect) — Fehlerfall restriktiv (keine Auto-Option).
  useEffect(() => {
    if (!clientId) { setDocTypeOptions([]); setUploadAutoDetect(false); return; }
    api.uploads.docTypes(clientId)
      .then(({ types, autoDetect }) => { setDocTypeOptions(types); setUploadAutoDetect(autoDetect); })
      .catch(() => { setDocTypeOptions([]); setUploadAutoDetect(false); });
  }, [clientId]);

  const executeUpload = async (files: File[], targetMonth: string, docType: string) => {
    setUploading(true);
    setUploadErrors([]);
    setUploadSummaries([]);
    setDuplicateDialog(null);
    const summaries: UploadSummary[] = [];
    const errs: string[] = [];
    let dup: DuplicateDialog | null = null;
    for (const file of files) {
      try {
        if (isXlsxFile(file)) {
          // Excel: synchroner Split in Karten je (MA × doc_type) — keine
          // OCR-Phase, daher kein Progress-Tracking; refresh() zeigt die Karten.
          const res = await api.uploads.xlsx(file, targetMonth, clientId);
          const zahl = (dt: string) => res.karten.filter((k) => k.doc_type === dt).length;
          summaries.push({
            filename: file.name,
            source: "excel",
            page_count_in: res.imported_rows,
            timesheets: zahl("zulagenblatt"),
            expenses: zahl("fahrtenbuch"),
            unknown: zahl("sonstige"),
            blank: 0,
            karten_count: res.karten_count,
            employees_matched: res.employees_matched,
          });
        } else {
          const res = await api.uploads.pdf(file, targetMonth, clientId, docType);
          summaries.push({ filename: file.name, page_count_in: res.page_count_in, ...res.summary });
          trackUpload(res.upload_id, file.name);
        }
      } catch (err: any) {
        if (err instanceof ApiError && err.status === 409 && err.body?.earlier_upload) {
          dup = { filename: file.name, earlier_upload: err.body.earlier_upload };
        } else {
          errs.push(`${file.name}: ${err.message}`);
        }
      }
    }
    setUploadSummaries(summaries);
    setUploadErrors(errs);
    if (dup) setDuplicateDialog(dup);
    setUploading(false);
    refresh();
  };

  const queueUpload = (files: File[]) => {
    if (files.length === 0) return;
    setPendingUpload({ files });
    setPendingMonth(month); // toolbar month as suggested default — operator overrides
    // #562-Rework: Default nur bei aktivierter Auto-Erkennung "" (= Auto);
    // sonst explizite Wahl — erster Mandanten-Typ vorausgewählt (Prod-Verhalten
    // „explizite Wahl autoritativ" bleibt für den Bestands-Mandanten unangetastet).
    setPendingDocType(uploadAutoDetect ? "" : (docTypeOptions[0]?.id ?? ""));
  };

  // #562-Folge (Anlassfall 2026-08-11, Franka/Zöscher): hat ein Mandant WEDER
  // registrierte Doc-Typen NOCH aktivierte Auto-Erkennung, kann er gar keinen
  // PDF-Upload annehmen — der Hub lehnt jede Datei mit auto_detect_disabled ab.
  // Bisher war der Typ-Selektor in dem Fall einfach leer und der Knopf aktiv:
  // der Operator erfuhr es erst NACH dem Klick, als Maschinen-Code. Der Zustand
  // gehört vor den Klick, sichtbar und mit gesperrtem Knopf.
  const typRegistryLeer = docTypeOptions.length === 0 && !uploadAutoDetect;
  const pendingHatPdf = !!pendingUpload?.files.some((f) => !isXlsxFile(f));
  const uploadGesperrt = pendingHatPdf && typRegistryLeer;

  const onUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = "";
    queueUpload(files);
  };

  const saveProcessMode = async (mode: "autonomous" | "manual") => {
    setProcessMode(mode);
    try {
      await api.preferences.patch({ process_mode: mode } as any);
    } catch (err: any) {
      alert(`Fehler: ${err.message}`);
    }
  };

  const startOcr = async (card: Card) => {
    try {
      const endpoint = card.kind === "timesheet"
        ? `api/timesheets/${card.id}/start-ocr`
        : `api/expenses/${card.id}/start-ocr`;
      const r = await fetch(new URL(endpoint, document.baseURI).toString(), {
        method: "POST",
        credentials: "same-origin",
      });
      if (!r.ok) throw new Error(humanServerError(await r.text()));
      refresh();
    } catch (err: any) {
      alert(`OCR starten fehlgeschlagen: ${err.message}`);
    }
  };

  const saveMergeMode = async (mode: "auto_with_banner" | "confirm") => {
    setMergeMode(mode);
    try {
      await api.preferences.patch({ diaet_merge_mode: mode });
    } catch (err: any) {
      alert(`Fehler: ${err.message}`);
    }
  };

  const toggleSelect = (card: Card) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(card.id)) next.delete(card.id);
      else next.add(card.id);
      return next;
    });
  };

  const batchValidate = async () => {
    if (selected.size === 0) return;
    // Partition by kind — backend needs homogeneous batches
    const selectedCards = cards.filter((c) => selected.has(c.id));
    const ts = selectedCards.filter((c) => c.kind === "timesheet").map((c) => c.id);
    const ex = selectedCards.filter((c) => c.kind === "expense").map((c) => c.id);
    setBatchBusy(true);
    try {
      let validated = 0;
      let errors: any[] = [];
      if (ts.length > 0) {
        const r = await api.batch.validate("timesheet", ts);
        validated += r.validated;
        errors = errors.concat(r.errors);
      }
      if (ex.length > 0) {
        const r = await api.batch.validate("expense", ex);
        validated += r.validated;
        errors = errors.concat(r.errors);
      }
      alert(`${validated} Karten validiert, ${errors.length} Fehler`);
      setSelected(new Set());
      refresh();
    } catch (e: any) {
      alert(`Fehler: ${e.message}`);
    } finally {
      setBatchBusy(false);
    }
  };

  const deleteCard = async (card: Card) => {
    if (!confirm(`"${card.pdf_orig_filename}" (${card.kind}) wirklich löschen?`)) return;
    try {
      const endpoint = card.kind === "timesheet"
        ? `api/timesheets/${card.id}`
        : `api/expenses/${card.id}`;
      await fetch(new URL(endpoint, document.baseURI).toString(), {
        method: "DELETE",
        credentials: "same-origin",
      });
      refresh();
    } catch (err: any) {
      alert(`Löschen fehlgeschlagen: ${err.message}`);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(
      (f) =>
        f.type === "application/pdf" ||
        f.name.toLowerCase().endsWith(".pdf") ||
        isXlsxFile(f),
    );
    queueUpload(files);
  };

  const undoMerge = async (mergedCardId: string) => {
    try {
      await api.expenseMerges.unmerge(mergedCardId);
      setMergeBanner((prev) => prev.filter((m) => m.merged_card_id !== mergedCardId));
      refresh();
    } catch (err: any) {
      alert(`Fehler: ${err.message}`);
    }
  };

  if (clientsLoaded && clients.length === 0) {
    return (
      <div className="kanban-page">
        <div className="empty-state">
          <h3>Kein aktiver Mandant</h3>
          <p className="muted">
            Es ist zurzeit kein Mandant aktiv. Bitte zuerst in den Einstellungen einen
            anlegen oder einen archivierten reaktivieren.
          </p>
          <Link to="/settings" className="primary" style={{ textDecoration: "none", padding: "8px 14px", display: "inline-block", borderRadius: 6 }}>
            Zu den Einstellungen
          </Link>
        </div>
      </div>
    );
  }

  const isSpecialView = typeFilter === "unknown" || typeFilter === "conflict";

  return (
    <div
      className={`kanban-page${dragOver ? " drop-active" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <div className="kanban-toolbar">
        <label className="field-inline">
          <span>Mandant</span>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
            {clients.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
        </label>
        <label className="field-inline">
          <span>Monat</span>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </label>
        <label className="upload-button">
          <input
            type="file"
            accept="application/pdf,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            multiple
            onChange={onUpload}
            disabled={uploading}
            style={{ display: "none" }}
          />
          <span>{uploading ? "Lade hoch…" : "Dateien hochladen"}</span>
        </label>
        {exportStatus && exportStatus.total > 0 && (
          <KanbanExportButtons
            month={month}
            clientId={clientId}
            status={exportStatus}
            onAfter={refresh}
          />
        )}
        {error && <span className="error">Fehler: {error}</span>}
        {loading && <span className="muted">Lade…</span>}
        <button
          type="button"
          className={`settings-btn${settingsOpen ? " active" : ""}`}
          onClick={() => setSettingsOpen(!settingsOpen)}
        >⚙ Einstellungen</button>
      </div>

      {settingsOpen && (
        <div className="settings-popover">
          <strong>Prozess-Modus</strong>
          <label>
            <input
              type="radio"
              name="processMode"
              checked={processMode === "autonomous"}
              onChange={() => saveProcessMode("autonomous")}
            />
            Autonom (Upload → OCR → automatisch)
          </label>
          <label>
            <input
              type="radio"
              name="processMode"
              checked={processMode === "manual"}
              onChange={() => saveProcessMode("manual")}
            />
            Manuell (Upload → warten → User klickt jeden Schritt)
          </label>
          <hr style={{ margin: "8px 0" }} />
          <strong>Diäten-Mehrseitigkeit</strong>
          <label>
            <input
              type="radio"
              name="mergeMode"
              checked={mergeMode === "auto_with_banner"}
              onChange={() => saveMergeMode("auto_with_banner")}
            />
            Automatisch zusammenführen + Hinweis (24 h)
          </label>
          <label>
            <input
              type="radio"
              name="mergeMode"
              checked={mergeMode === "confirm"}
              onChange={() => saveMergeMode("confirm")}
            />
            Jede Zusammenführung vorher bestätigen
          </label>
          <button className="link" onClick={() => setSettingsOpen(false)}>Schließen</button>
        </div>
      )}

      <div className="type-tabs">
        {filterTabs.map((t) => {
          const active = t.id === typeFilter;
          // Tab-Badge-Counter aus dem Server-Aggregat (Single Source, kein
          // Zählen aus dem gefilterten `cards`-Array). doc_type-Tabs lesen ihren
          // Zähler aus by_doc_type[id]; die Sonder-Tabs aus total/unknown/conflict.
          const count: number =
            t.kind === "all"
              ? typeCounts.total
              : t.kind === "conflict"
              ? typeCounts.conflict
              : t.kind === "unknown"
              ? typeCounts.unknown
              : (typeCounts.by_doc_type[t.id] ?? 0);
          return (
            <button
              key={t.id}
              className={`type-tab${active ? " active" : ""}${t.kind === "conflict" && count > 0 ? " conflict-badge" : ""}${t.inactive ? " type-tab-inactive" : ""}`}
              onClick={() => setTypeFilter(t.id)}
              title={t.inactive
                ? `Doc-Typ „${t.id}" ist beim Mandanten nicht mehr registriert, kommt aber noch auf Karten vor`
                : undefined}
            >
              {t.label}{t.inactive && " (inaktiv)"}
              {count > 0 && <span className="type-tab-count">{count}</span>}
            </button>
          );
        })}
        {/* Bug 2 (Thomas 24.07.): Quelle-Filter Excel vs PDF — als zweite
            Filter-Achse neben den Typ-Tabs (wirkt auf die Spalten-Karten). */}
        <span style={{ marginLeft: "auto", display: "inline-flex", gap: 4, alignItems: "center", fontSize: 12 }}>
          <span className="muted">Quelle:</span>
          {([["alle", "Alle"], ["pdf", "PDF"], ["excel", "Excel"]] as const).map(([id, label]) => (
            <button
              key={id}
              className={`type-tab${quelleFilter === id ? " active" : ""}`}
              onClick={() => setQuelleFilter(id)}
              title={id === "excel" ? "Nur Karten aus dem Excel-Import"
                : id === "pdf" ? "Nur Karten aus PDF/Scan-Uploads" : "Alle Quellen"}
            >
              {label}
            </button>
          ))}
        </span>
      </div>

      {pendingUpload && (
        <div className="duplicate-dialog">
          <h3>Für welchen Monat?</h3>
          <p>
            {pendingUpload.files.length === 1
              ? <>1 Datei: <strong>{pendingUpload.files[0]!.name}</strong></>
              : <>{pendingUpload.files.length} Dateien ausgewählt</>}
          </p>
          <p className="muted">
            Die Zuordnung zum Abrechnungs-Monat ist eine bewusste Entscheidung —
            keine Auto-Inferenz aus dem Datum oder dem Toolbar-Filter.
          </p>
          {uploadGesperrt && (
            <p className="upload-blocked">
              <strong>Für diesen Mandanten sind keine Dokumenttypen registriert.</strong>{" "}
              Ein PDF-Upload ist deshalb nicht möglich — das System könnte die Seiten
              keiner Pipeline zuordnen. Bitte die Dokumenttypen des Mandanten
              hinterlegen lassen; danach funktioniert der Upload wie gewohnt.
            </p>
          )}
          <div style={{ display: "flex", gap: 12, alignItems: "center", margin: "12px 0", flexWrap: "wrap" }}>
            {pendingUpload.files.some((f) => !isXlsxFile(f)) && (
              <label className="field-inline">
                <span>Typ</span>
                <select
                  value={pendingDocType}
                  onChange={(e) => setPendingDocType(e.target.value)}
                  autoFocus
                >
                  {/* #562-Rework („Sichtbarkeit = Lieferversprechen"): die Auto-Option
                      erscheint NUR bei Mandanten mit aktiviertem upload_auto_detect
                      (Regelwerk, z. B. 3g). Für alle anderen gilt die explizite Wahl —
                      der Hub lehnt Auto dort zusätzlich serverseitig laut ab. Für
                      .xlsx ist der Selektor ausgeblendet — Excel wird je Datensatz
                      über die Art typisiert. */}
                  {uploadAutoDetect && <option value="">— Automatisch erkennen —</option>}
                  {docTypeOptions.map((d) => (
                    <option key={d.id} value={d.id}>{d.label}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="field-inline">
              <span>Monat</span>
              <input
                type="month"
                value={pendingMonth}
                onChange={(e) => setPendingMonth(e.target.value)}
              />
            </label>
          </div>
          <div className="dup-actions">
            <button className="link" onClick={() => setPendingUpload(null)}>Abbrechen</button>
            <button
              className="primary"
              disabled={!/^\d{4}-(0[1-9]|1[0-2])$/.test(pendingMonth) || uploadGesperrt}
              onClick={async () => {
                const files = pendingUpload.files;
                const m = pendingMonth;
                const dt = pendingDocType;
                setPendingUpload(null);
                await executeUpload(files, m, dt);
              }}
            >
              Hochladen für {pendingMonth || "—"}
            </button>
          </div>
        </div>
      )}

      {duplicateDialog && (
        <div className="duplicate-dialog">
          <h3>Dieses PDF wurde bereits hochgeladen</h3>
          <p>
            <strong>{duplicateDialog.filename}</strong> — hochgeladen am{" "}
            {new Date(duplicateDialog.earlier_upload.uploaded_at).toLocaleString("de-DE")}
            {" "}durch <em>{duplicateDialog.earlier_upload.by_user}</em>.
          </p>
          <p className="muted">Ergebnis: {duplicateDialog.earlier_upload.cards.length} Karten</p>
          <ul className="dup-cards">
            {duplicateDialog.earlier_upload.cards.map((c) => (
              <li key={c.id}>
                <Link to={c.kind === "timesheet" ? `/timesheets/${c.id}` : `/expenses/${c.id}`}>
                  {c.kind === "timesheet" ? "🕐 Stunden"
                    : c.kind === "expense" ? "🧳 Diät"
                    : c.kind === "excel_import" ? "Excel-Import"
                    : "❓"}
                  {" "}· {c.month} · <em>{c.state}</em>
                </Link>
              </li>
            ))}
          </ul>
          <div className="dup-actions">
            <button className="link" onClick={() => setDuplicateDialog(null)}>Schließen</button>
            <button
              className="danger-sm"
              onClick={async () => {
                const cards = duplicateDialog.earlier_upload.cards;
                try {
                  for (const c of cards) {
                    if (c.kind === "timesheet") {
                      await api.timesheets.delete(c.id);
                    } else {
                      await fetch(`api/expenses/${c.id}`, { method: "DELETE", credentials: "same-origin" });
                    }
                  }
                  setDuplicateDialog(null);
                  refresh();
                } catch (err: any) {
                  alert(`Fehler beim Löschen: ${err.message}`);
                }
              }}
            >
              {duplicateDialog.earlier_upload.cards.length === 1 ? "Karte löschen" : `${duplicateDialog.earlier_upload.cards.length} Karten löschen`}
            </button>
          </div>
        </div>
      )}

      {mergeBanner.length > 0 && (
        <div className="merge-banner">
          <div>
            ℹ {mergeBanner.length} Diät-{mergeBanner.length === 1 ? "Seite" : "Seiten"} in den letzten 24 h automatisch zusammengeführt:
          </div>
          <ul>
            {mergeBanner.map((m) => (
              <li key={m.merged_card_id}>
                {m.last_name && m.first_name ? `${m.last_name}, ${m.first_name}` : "—"}
                {" · "}{m.month}
                {" "}<Link to={`/expenses/${m.target_card_id}`}>[Ansehen]</Link>
                {" "}<button className="link" onClick={() => undoMerge(m.merged_card_id)}>[Rückgängig]</button>
              </li>
            ))}
          </ul>
          <button className="link" onClick={() => setMergeBanner([])}>Hinweise schließen</button>
        </div>
      )}

      {selected.size > 0 && (
        <div className="batch-toolbar">
          <strong>{selected.size} Karten ausgewählt</strong>
          <button className="primary success" disabled={batchBusy} onClick={batchValidate}>
            {batchBusy ? "…" : `${selected.size} Karten validieren`}
          </button>
          <button className="link" onClick={() => setSelected(new Set())}>Auswahl aufheben</button>
        </div>
      )}

      {activeUploads.length > 0 && (
        <div className="active-uploads">
          {activeUploads.map((u) => (
            <UploadProgressBanner
              key={u.id}
              uploadId={u.id}
              filename={u.filename}
              onDone={() => untrackUpload(u.id)}
              onDismiss={() => untrackUpload(u.id)}
            />
          ))}
        </div>
      )}

      {uploadSummaries.length > 0 && (
        <div className="upload-summary">
          {uploadSummaries.map((s, i) => (
            <div key={i} className="upload-summary-row">
              <strong>✓ {s.filename}</strong>
              {s.source === "excel" ? (
                <>
                  <span>
                    {s.page_count_in} Zeilen → {s.karten_count} Karten
                    {s.employees_matched != null ? ` (${s.employees_matched} Mitarbeiter)` : ""}:
                  </span>
                  <span>{s.timesheets} Zulagen</span>
                  <span>· {s.expenses} Reisekosten</span>
                  {s.unknown > 0 && <span>· {s.unknown} Sonstige</span>}
                </>
              ) : (
                <>
                  <span>{s.page_count_in} Seiten verarbeitet:</span>
                  <span>{s.timesheets} Stunden</span>
                  <span>· {s.expenses} Diäten</span>
                  {s.unknown > 0 && <span>· {s.unknown} unklar</span>}
                  {s.blank > 0 && <span className="muted">· {s.blank} leer übersprungen</span>}
                </>
              )}
            </div>
          ))}
          <button className="link" onClick={() => setUploadSummaries([])}>Schließen</button>
        </div>
      )}

      {uploadErrors.length > 0 && (
        <div className="error-box">
          <strong>Upload-Probleme:</strong>
          <ul>{uploadErrors.map((e, i) => (<li key={i}>{e}</li>))}</ul>
        </div>
      )}

      {isSpecialView ? (
        <SpecialLane cards={cards} typeFilter={typeFilter} onReclassify={refresh} isDeveloper={isDeveloper} docTypeOptions={docTypeOptions} />
      ) : cards.length === 0 && !loading ? (
        <BoardEmptyState
          month={month}
          monthsWithWork={attention.months_with_work}
          onPickMonth={setMonth}
        />
      ) : (
        <KanbanLayout
          docTypeOptions={docTypeOptions}
          byColumn={byColumn}
          selected={selected}
          onToggleSelect={toggleSelect}
          onDelete={deleteCard}
          onStartOcr={startOcr}
          isDeveloper={isDeveloper}
        />
      )}

      <ArchivedSection
        docTypeOptions={docTypeOptions}
        expanded={archiveExpanded}
        onToggle={() => setArchiveExpanded(v => !v)}
        cards={archivedCards}
        archivedCount={typeCounts.archived}
        typeFilter={typeFilter}
        selected={selected}
        onToggleSelect={toggleSelect}
        onDelete={deleteCard}
        onStartOcr={startOcr}
        isDeveloper={isDeveloper}
      />
    </div>
  );
}

/**
 * Archive-Drawer: bottom-fixed Drawer (position: fixed; bottom: 0). Toggle
 * IMMER am Viewport-Boden sichtbar — unabhängig von Scroll-Container, kein
 * Sticky-CSS-Trick. Aufgeklappt: drawer schiebt hoch (max 50vh), Body scrollt
 * intern. Glass-Box-Lücke geschlossen (2026-05-21) — archivierte/superseded
 * Karten hatten vorher KEINE UI-Adresse.
 *
 * .kanban-page bekommt padding-bottom damit der Drawer keine Karten überlagert.
 *
 * Lazy: Polling-Hook ist disabled solange `expanded=false` — null Bandbreite.
 * Type-Filter (Stunden/Diäten/Unklar) wirkt analog zu den Hauptspalten.
 */
const ARCHIVE_DRAWER_TOGGLE_HEIGHT = 44;  // px — auch in kanban-page padding-bottom
function ArchivedSection({
  expanded,
  onToggle,
  cards,
  archivedCount,
  typeFilter,
  selected,
  onToggleSelect,
  onDelete,
  onStartOcr,
  isDeveloper = false,
  docTypeOptions = [],
}: {
  expanded: boolean;
  onToggle: () => void;
  cards: Card[];
  archivedCount: number;
  typeFilter: CardTypeFilter;
  selected: Set<string>;
  onToggleSelect: (c: Card) => void;
  onDelete: (c: Card) => void;
  onStartOcr: (c: Card) => void;
  isDeveloper?: boolean;
  docTypeOptions?: Array<{ id: string; label: string; routesTo: "timesheet" | "expense" }>;
}) {
  if (archivedCount === 0 && cards.length === 0) return null;
  return (
    <aside
      className="kanban-archive-drawer"
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        background: "var(--surface, #ffffff)",
        borderTop: "1px solid #d1d5db",
        boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.08)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        style={{
          height: ARCHIVE_DRAWER_TOGGLE_HEIGHT,
          width: "100%",
          padding: "0 16px",
          background: expanded ? "#e5e7eb" : "#f3f4f6",
          border: "none",
          borderBottom: expanded ? "1px solid #d1d5db" : "none",
          cursor: "pointer",
          textAlign: "left",
          fontSize: 13,
          color: "#374151",
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span style={{ fontSize: 12, color: "#6b7280", width: 14 }}>{expanded ? "▼" : "▲"}</span>
        <strong>Archiviert</strong>
        <span style={{
          background: "#9ca3af", color: "white",
          borderRadius: 10, padding: "1px 8px",
          fontSize: 11, fontWeight: 600,
        }}>
          {archivedCount}
        </span>
        <span style={{ color: "#6b7280", fontSize: 12, marginLeft: "auto" }}>
          {expanded ? "Klick zum Zuklappen" : "archivierte und superseded Karten anzeigen"}
        </span>
      </button>
      {expanded && (
        <div
          className="kanban-archive-body"
          style={{
            // 50vh - toggle-height = drawer-content-height
            maxHeight: `calc(50vh - ${ARCHIVE_DRAWER_TOGGLE_HEIGHT}px)`,
            overflowY: "auto",
            padding: 12,
            background: "#fafafa",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 10,
          }}
        >
          {cards.length === 0 ? (
            <div style={{ color: "#6b7280", fontStyle: "italic", fontSize: 13 }}>
              {typeFilter === "all"
                ? "Keine archivierten Karten in diesem Monat."
                : "Keine archivierten Karten in diesem Filter."}
            </div>
          ) : (
            cards.map((c) => (
              <KanbanCard
                docTypeOptions={docTypeOptions}
                key={c.id}
                card={c}
                selected={selected.has(c.id)}
                onToggleSelect={onToggleSelect}
                onDelete={onDelete}
                onStartOcr={onStartOcr}
                isDeveloper={isDeveloper}
              />
            ))
          )}
        </div>
      )}
    </aside>
  );
}

function CardIdChip({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  const onClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* clipboard may be blocked */ }
  };
  return (
    <span
      className="card-id-chip"
      title={copied ? "Kopiert!" : `${id} — klick zum Kopieren`}
      onClick={onClick}
    >
      {copied ? "✓" : `#${id.slice(0, 8)}`}
    </span>
  );
}

/**
 * Adaptive layout: same KanbanCard component on all screens; only the column
 * arrangement differs.
 *
 *   desktop  → 6 horizontal columns (existing layout)
 *   tablet   → 3 columns × 2 rows
 *   mobile   → single column at a time, switched via top tabs
 *
 * Anti-drift: the KanbanCard is rendered identically on all screens. Only
 * this layout component branches. Adding a new column/state means editing
 * `COLUMNS` once.
 */
/** Top-of-board empty state. Mirrors the inbox so the operator sees a
 *  consistent "🎉 Alles erledigt" + month-jump UX whichever tab they're on.
 *  months_with_work is reused from the attention payload (open work only) —
 *  pragmatic for TEST phase; can be widened to "any cards" later if needed. */
function BoardEmptyState({
  month,
  monthsWithWork,
  onPickMonth,
}: {
  month: string;
  monthsWithWork: Array<{ month: string; open_count: number }>;
  onPickMonth: (m: string) => void;
}) {
  return (
    <div className="empty-state">
      <h3>🎉 Alles erledigt</h3>
      <p className="muted">Keine offenen Aktionen für {month}. Upload eines PDFs startet die Pipeline.</p>
      {monthsWithWork.length > 0 && (
        <div className="empty-state-hint">
          <p className="muted">Andere Monate mit offenen Karten:</p>
          <div className="month-hint-chips">
            {monthsWithWork.map((m) => (
              <button
                key={m.month}
                type="button"
                className="month-hint-chip"
                onClick={() => onPickMonth(m.month)}
              >
                {m.month} · <strong>{m.open_count}</strong>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KanbanLayout({
  byColumn,
  selected,
  onToggleSelect,
  onDelete,
  onStartOcr,
  isDeveloper = false,
  docTypeOptions = [],
}: {
  byColumn: Map<string, Card[]>;
  selected: Set<string>;
  onToggleSelect: (c: Card) => void;
  onDelete: (c: Card) => void;
  onStartOcr: (c: Card) => void;
  isDeveloper?: boolean;
  docTypeOptions?: Array<{ id: string; label: string; routesTo: "timesheet" | "expense" }>;
}) {
  const vp = useViewport();
  const [activeColumn, setActiveColumn] = useState(() => {
    // Land on the first non-empty column on mobile so users see work immediately
    return COLUMNS.find((c) => (byColumn.get(c.state) ?? []).length > 0)?.state
      ?? COLUMNS[0]!.state;
  });

  // Karten innerhalb einer Spalte pro Mitarbeiter gruppieren + sortieren
  // (2026-06-15): Diäten können MEHRERE Karten pro MA/Monat haben. Sie sollen
  // optisch einem MA zugeordnet erscheinen — analog zur Export-Aggregation
  // (1 Zeile/MA) — damit eine optische Vollständigkeitsprüfung möglich ist.
  // Reihenfolge: MA alphabetisch, "ohne Zuordnung" zuletzt; innerhalb der
  // Gruppe nach Scan-Seite (Reise-Reihenfolge).
  const groupByEmployee = (cs: Card[]) => {
    const byKey = new Map<string, Card[]>();
    for (const c of cs) {
      const key = c.employee_id ?? "__none__";
      const arr = byKey.get(key);
      if (arr) arr.push(c);
      else byKey.set(key, [c]);
    }
    const groups = [...byKey.entries()].map(([key, cards]) => {
      const c0 = cards[0]!;
      const none = key === "__none__";
      const label = !none && c0.last_name && c0.first_name
        ? `${c0.last_name}, ${c0.first_name}`
        : "— ohne Zuordnung —";
      const sortName = !none && c0.last_name ? c0.last_name.toLowerCase() : "￿";
      cards.sort((a, b) =>
        (((a as any).source_page_index ?? 0) - ((b as any).source_page_index ?? 0))
        || a.month.localeCompare(b.month));
      return { key, label, count: cards.length, cards, sortName, none };
    });
    groups.sort((a, b) =>
      a.none !== b.none ? (a.none ? 1 : -1) : a.sortName.localeCompare(b.sortName));
    return groups;
  };

  const renderColumn = (col: typeof COLUMNS[number]) => {
    const cs = byColumn.get(col.state) ?? [];
    const groups = groupByEmployee(cs);
    return (
      <section key={col.state} className="kanban-col">
        <header className="kanban-col-header">
          {col.label} <span className="kanban-col-count">{cs.length}</span>
        </header>
        <div className="kanban-col-body">
          {cs.length === 0 ? (
            <div className="kanban-empty">
              {col.state === "uploaded" || col.state === "ocr_pending"
                ? "PDFs hochladen →"
                : "leer"}
            </div>
          ) : (
            groups.map((g) => (
              <div key={g.key} className={`kanban-ma-group${g.none ? " kanban-ma-group-none" : ""}`}>
                <div
                  className="kanban-ma-head"
                  title={g.none ? "Noch keinem Mitarbeiter zugeordnet" : `${g.count} Karte(n) für ${g.label}`}
                >
                  <span className="kanban-ma-name">{g.label}</span>
                  <span className="kanban-ma-count">{g.count}</span>
                </div>
                {g.cards.map((c) => (
                  <KanbanCard
                    docTypeOptions={docTypeOptions}
                    key={c.id}
                    card={c}
                    selected={selected.has(c.id)}
                    onToggleSelect={onToggleSelect}
                    onDelete={onDelete}
                    onStartOcr={onStartOcr}
                    isDeveloper={isDeveloper === true}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      </section>
    );
  };

  if (vp === "mobile") {
    return (
      <div className="kanban kanban-mobile">
        <nav className="kanban-mobile-tabs" role="tablist">
          {COLUMNS.map((col) => {
            const n = byColumn.get(col.state)?.length ?? 0;
            const active = col.state === activeColumn;
            return (
              <button
                key={col.state}
                role="tab"
                aria-selected={active}
                className={`kanban-mobile-tab${active ? " active" : ""}`}
                onClick={() => setActiveColumn(col.state)}
              >
                <span className="kanban-mobile-tab-label">{col.label}</span>
                {n > 0 && <span className="kanban-mobile-tab-count">{n}</span>}
              </button>
            );
          })}
        </nav>
        {COLUMNS.filter((c) => c.state === activeColumn).map(renderColumn)}
      </div>
    );
  }

  // tablet + desktop: same multi-column grid (CSS handles the column count)
  return (
    <div className={`kanban kanban-${vp}`}>
      {COLUMNS.map(renderColumn)}
    </div>
  );
}

/** Live progress display for cards in ocr_pending / ocr_running.
 *  Single source: lib/ocrProgress.ts — gleiche Berechnung wie Werkstatt-RunInspector,
 *  fetched live avg_runtime_s aus /api/queue/status. Vorher hardcoded TARGET_S=360
 *  + 99%-Cap — das wich von der Werkstatt ab und war Glass-Box-Bruch. */
function OcrProgress({ card }: { card: Card }) {
  const [now, setNow] = useState(Date.now());
  const avgRuntimeS = useQueueAvgRuntime();
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const retryCount = card.ocr_retry_count ?? 0;
  const isRetry = retryCount > 0 || card.ocr_queue_state === "retry";

  // Stuck-Pfad: server-computed (state=ocr_running + updated_at > 15min alt).
  // Ersetzt die "läuft"-Anzeige durch einen klaren Stopp-Hinweis + Restart-
  // Button. Verhindert dass der Progress-Bar weiterzählt während pg-boss
  // den Job längst aufgegeben hat (2026-05-19 Anlassfall b65cecfe).
  if (card.stuck_ocr) {
    return <StuckCardCue card={card} />;
  }

  if (card.state === "ocr_pending") {
    if (isRetry) {
      return (
        <div className="ocr-progress retry" title={`pg-boss retry_count=${retryCount}`}>
          <span>↻ Im Retry — System versucht es erneut{retryCount > 0 ? ` (Versuch ${retryCount + 1})` : ""}</span>
        </div>
      );
    }
    return (
      <div className="ocr-progress queued" title="In der Warteschlange">
        <span>⏳ in Warteschlange</span>
      </div>
    );
  }
  // ocr_running: show elapsed time + animated progress bar — same calc as Werkstatt
  const startedMs = new Date(card.ocr_started_at ?? card.updated_at).getTime();
  const progress = computeOcrProgress(now - startedMs, avgRuntimeS ? avgRuntimeS * 1000 : null);
  const cls = `${isRetry ? "ocr-progress running retrying" : "ocr-progress running"}${progress.overBudget ? " over-budget" : ""}`;
  return (
    <div className={cls} title={`OCR läuft seit ${progress.elapsedS}s${isRetry ? ` · Retry ${retryCount + 1}` : ""}${progress.overBudget ? " · über Durchschnitt" : ""}`}>
      <div className="ocr-progress-label">
        {isRetry ? "↻ Retry läuft" : "🔄 OCR läuft"}
        {" · "}{formatElapsed(progress.elapsedS)}
        {/* Kein %-Wert (die Zeit-Schätzung überschritt „100%" = irreführend, #473);
            nur die verstrichene Zeit + ein Hinweis, wenn es länger als üblich dauert. */}
        {progress.overBudget && (
          <span className="ocr-progress-pct" style={{ color: "#dc2626", fontWeight: 600 }}>
            {" · "}länger als üblich
          </span>
        )}
      </div>
      <div className="ocr-progress-bar">
        <div
          className="ocr-progress-bar-fill"
          style={{
            width: `${progress.barPct}%`,
            background: progress.overBudget ? "#dc2626" : undefined,
          }}
        />
      </div>
    </div>
  );
}

/** Stuck-OCR Indicator + Restart-Button auf der Kanban-Karte. Sichtbar wenn
 *  card.stuck_ocr=true (server-computed). Klick auf Button stoppt event
 *  propagation damit der Detail-Link NICHT mitgeöffnet wird. */
function StuckCardCue({ card }: { card: Card }) {
  const [phase, setPhase] = useState<"idle" | "restarting" | "done" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const stuckMin = Math.round(card.stuck_minutes ?? 0);

  async function restart(e: MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    setPhase("restarting");
    setError(null);
    try {
      await api.cards.restartOcr(card.id);
      setPhase("done");
    } catch (err: any) {
      setError(err?.message ?? String(err));
      setPhase("error");
    }
  }

  return (
    <div
      className="ocr-progress stuck"
      title={`OCR-Handler ist mitten in der Verarbeitung verstummt (typisch: OCR-Sidecar-Restart genau während des Calls). Restart setzt state zurück auf ocr_pending und enqueued einen neuen Job.`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="ocr-progress-label" style={{ color: "#7f1d1d", fontWeight: 600 }}>
        ⏸ OCR gestoppt seit {stuckMin} min
      </div>
      <button
        className="card-action-btn"
        onClick={restart}
        disabled={phase === "restarting"}
        style={{
          marginTop: 4,
          background: phase === "restarting" ? "#9ca3af" : "#b91c1c",
          color: "white",
        }}
      >
        {phase === "restarting" ? "Restart läuft …"
          : phase === "done" ? "✅ Restart ausgelöst"
          : "OCR neu starten"}
      </button>
      {phase === "error" && error && (
        <div style={{ color: "#991b1b", fontSize: 11, marginTop: 4 }}>Fehler: {error}</div>
      )}
    </div>
  );
}

function KanbanCard({
  card,
  selected,
  onToggleSelect,
  onDelete,
  onStartOcr,
  isDeveloper = false,
  docTypeOptions = [],
}: {
  card: Card;
  selected?: boolean;
  onToggleSelect?: (c: Card) => void;
  onDelete?: (c: Card) => void;
  onStartOcr?: (c: Card) => void;
  isDeveloper?: boolean;
  /** Mandanten-Typ-Registry — die kind-Pille spricht Mandanten-Sprache
   *  (Thomas 24.07.: "Std"/"Diät" waren Bestand-Kürzel, bei 3g falsch). */
  docTypeOptions?: Array<{ id: string; label: string; routesTo: "timesheet" | "expense" }>;
}) {
  // Pass current location as background so the detail route renders as an
  // overlay over the board (scroll position preserved). Direct refresh on
  // the detail URL still works: no background → full-page render.
  const location = useLocation();
  const navigate = useNavigate();
  const linkState = { background: location };
  const when = new Date(card.created_at);
  // Ganze Karte klickbar (2026-05-19): vorher hatten nur Name+Meta-Link Click-
  // Handler — Klicks auf Tier-Badge, OcrProgress, leeren Bereich machten
  // NICHTS (kein Feedback, User dachte "Karte öffnet nicht"). Jetzt navigiert
  // die ganze Karte zur Detail-View, außer auf Elemente die schon eigene
  // Handler haben (a/button/input — Name-Link, Delete-Button, Checkbox usw).
  const onCardClick = (e: React.MouseEvent) => {
    const t = e.target as HTMLElement;
    if (t.closest("a, button, input, [role='button']")) return;
    navigate(cardLink(card), { state: linkState });
  };
  const label =
    card.first_name && card.last_name ? `${card.last_name}, ${card.first_name}` : null;
  const confidence = aggregateConfidence(card.kind, card.data);
  const bucket = bucketOf(confidence);
  const hasOcrError = !!(card.data as any)?.ocr_error;
  const selectable =
    card.state === "ocr_complete" && card.employee_id && bucket === "green";
  return (
    <div
      className={`kanban-card kind-${card.kind} tl-${bucket}${selected ? " selected" : ""}`}
      onClick={onCardClick}
      style={{ cursor: "pointer" }}
    >
      <div className="kanban-card-head">
        {selectable && onToggleSelect && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={(e) => { e.stopPropagation(); onToggleSelect(card); }}
            onClick={(e) => e.stopPropagation()}
            title="Für Batch-Validierung auswählen"
          />
        )}
        {/* Mandanten-dynamische kind-Pille (Thomas 24.07.): zeigt der Karte
            bereits ein doc_type-Badge (Zulagenblatt/Fahrtenbuch unten), ist
            die generische Pille redundant und ENTFÄLLT; sonst kommt der Name
            aus der Registry (eindeutige Route je kind, Kurzform ohne
            Klammer-Zusatz) — Bestand liest sich "Stundenliste"/"Diäten". */}
        {!isZulagenblattData(card.data) && !isFahrtenbuchData(card.data) && (() => {
          const route = card.kind === "timesheet" || card.kind === "expense" ? card.kind : null;
          const ziele = route ? docTypeOptions.filter((d) => d.routesTo === route) : [];
          const name = ziele.length === 1
            ? ziele[0]!.label.replace(/\s*\(.*$/, "")
            : kindLabel(card.kind);
          return (
            <span className={`kind-pill kind-pill-${card.kind}`} title={`Kartentyp: ${name}`}>
              {`${kindIcon(card.kind)} ${name}`.trim()}
            </span>
          );
        })()}
        {/* T13: Zulagenblatt-Karten sind kind=timesheet mit eigenem doc_type —
         *  eigenes Badge, damit sie im Board von Stundenlisten unterscheidbar sind. */}
        {isZulagenblattData(card.data) && (
          <span className="kind-pill kind-pill-zulagenblatt" title="Dokumenttyp: Zulagenblatt (Mandant 3g)">
            Zulagenblatt
          </span>
        )}
        {/* card #523: Fahrtenbuch-Karten sind kind=expense mit doc_type=fahrtenbuch —
         *  eigenes Badge (Spiegel des Zulagenblatt-Badges), damit sie im Board von
         *  generischen Diäten unterscheidbar sind. */}
        {isFahrtenbuchData(card.data) && (
          <span className="kind-pill kind-pill-fahrtenbuch" title="Dokumenttyp: Fahrtenbuch (km-Zeilen)">
            Fahrtenbuch
          </span>
        )}
        {/* Quelle-Pille (Thomas 24.07.): Excel-Import-Karten tragen denselben
            doc_type wie PDF-Karten (Zulagenblatt/Fahrtenbuch) — erst die
            Quelle macht sie am Board unterscheidbar (passend zum
            Quelle-Filter oben). */}
        <span
          className="kind-pill"
          title={card.kind === "excel_import" ? "Quelle: Excel-Import" : "Quelle: PDF/Scan-Upload"}
          style={card.kind === "excel_import"
            ? { background: "#dcfce7", color: "#166534", border: "1px solid #86efac" }
            : { background: "#e0e7ff", color: "#3730a3", border: "1px solid #a5b4fc" }}
        >
          {card.kind === "excel_import" ? "Excel" : "PDF"}
        </span>
        <CardIdChip id={card.id} />
        {(() => { const t = cardMaxTier(card.data); return t ? <TierBadge tier={t} /> : null; })()}
        <Link to={cardLink(card)} state={linkState} className="kanban-card-name">
          {label ?? <span className="muted">Nicht zugeordnet</span>}
        </Link>
        {hasOcrError && <span title="OCR-Fehler" style={{ fontSize: 11 }}>⚠</span>}
        {card.missing_schedule && (
          <span
            title="Mitarbeiter hat keine Sollzeiten konfiguriert — Validieren nicht möglich. Erst Wochenarbeitszeit in den Mitarbeiter-Einstellungen hinterlegen."
            style={{ fontSize: 13, color: "#d97706", cursor: "help" }}
          >
            ⚠️
          </span>
        )}
        {onDelete && (
          <button
            className="card-delete"
            title="Karte löschen"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDelete(card); }}
          >✕</button>
        )}
      </div>
      {card.state === "uploaded" && onStartOcr && (
        <button
          className="card-action-btn"
          onClick={(e) => { e.stopPropagation(); onStartOcr(card); }}
        >▶ OCR starten</button>
      )}
      {(card.state === "ocr_pending" || card.state === "ocr_running") && (
        <OcrProgress card={card} />
      )}
      <Link to={cardLink(card)} state={linkState} className="kanban-card-meta-link">
        <div className="kanban-card-meta">{card.pdf_orig_filename}</div>
        <div className="kanban-card-foot">
          {when.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
          {" · "}
          {Math.round(card.pdf_bytes / 1024)} KB
          {confidence !== null && (
            <span className="confidence-badge">
              {" · "}OCR {Math.round(confidence * 100)}%
            </span>
          )}
        </div>
      </Link>
      {isDeveloper && (
        <Link
          to={werkstattLink(card)}
          state={linkState}
          className="kanban-card-werkstatt-link"
          data-perm="kanban-werkstatt-link"
          title="Pipeline-Werkstatt für diese Karte öffnen — Modal, schließen kehrt zurück (developer-only)"
          style={{ fontSize: 11, color: "#1d4ed8", textDecoration: "none", marginTop: 4 }}
        >
          🔬 Werkstatt →
        </Link>
      )}
    </div>
  );
}

function SpecialLane({
  cards,
  typeFilter,
  onReclassify,
  isDeveloper = false,
  docTypeOptions = [],
}: {
  cards: Card[];
  typeFilter: CardTypeFilter;
  onReclassify: () => void;
  isDeveloper?: boolean;
  docTypeOptions?: Array<{ id: string; label: string; routesTo: "timesheet" | "expense" }>;
}) {
  const title =
    typeFilter === "conflict"
      ? "Konflikte — User-Entscheidung erforderlich"
      : "Unklare Seiten — Klassifikation prüfen";
  const empty =
    typeFilter === "conflict"
      ? "Keine Konflikte. 🎉"
      : "Keine unklaren Seiten.";

  if (cards.length === 0) {
    return (
      <section className="special-lane">
        <h3>{title}</h3>
        <div className="muted">{empty}</div>
      </section>
    );
  }

  if (typeFilter === "unknown") {
    return (
      <section className="special-lane">
        <h3>{title}</h3>
        <div className="special-lane-list">
          {cards.map((c) => (
            <UnknownCard key={c.id} card={c} onChanged={onReclassify} isDeveloper={isDeveloper} docTypeOptions={docTypeOptions} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="special-lane">
      <h3>{title}</h3>
      <div className="special-lane-list">
        {cards.map((c) => <KanbanCard key={c.id} card={c} isDeveloper={isDeveloper} docTypeOptions={docTypeOptions} />)}
      </div>
    </section>
  );
}

function UnknownCard({ card, onChanged, isDeveloper = false, docTypeOptions = [] }: { card: Card; onChanged: () => void; isDeveloper?: boolean; docTypeOptions?: Array<{ id: string; label: string; routesTo: "timesheet" | "expense" }> }) {
  const [busy, setBusy] = useState(false);

  // #563: eine unklare/Sonstige-Karte per Deklaration in einen bekannten
  // doc_type überführen (Zulage/Reisekosten) — client-doc_types, kein Hardcode.
  const setCardDocType = async (docType: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(
        new URL(`api/cards/expense/${card.id}/set-doc-type`, document.baseURI).toString(),
        { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ doc_type: docType }) },
      );
      if (!r.ok) throw new Error(humanServerError(await r.text()));
      onChanged();
    } catch (e: any) {
      alert(`Fehler: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const reclassify = async (target: "timesheet" | "expense", docType?: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(
        new URL(`api/expenses/${card.id}/reclassify`, document.baseURI).toString(),
        {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(docType ? { target, doc_type: docType } : { target }),
        },
      );
      if (!r.ok) throw new Error(humanServerError(await r.text()));
      onChanged();
    } catch (e: any) {
      alert(`Fehler: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const archive = async () => {
    if (!confirm(`"${card.pdf_orig_filename}" archivieren? (Original-PDF bleibt.)`)) return;
    setBusy(true);
    try {
      const r = await fetch(
        new URL(`api/expenses/${card.id}/archive`, document.baseURI).toString(),
        { method: "POST", credentials: "same-origin" },
      );
      if (!r.ok) throw new Error(humanServerError(await r.text()));
      onChanged();
    } catch (e: any) {
      alert(`Fehler: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const loc = useLocation();
  const linkState = { background: loc };
  return (
    <div className={`kanban-card kind-${card.kind}`}>
      <div className="kanban-card-head">
        <span className="kind-badge">❓</span>
        <CardIdChip id={card.id} />
        {/* Filename verlinkt auf die ExpenseDetailPage — die hat einen
            kind=unknown-Branch (Banner + Buttons), zeigt das PDF-Crop und
            erlaubt vor dem Reclassify einen Blick auf die Seite. Vorher
            war hier nur ein div, Click ging ins Leere. */}
        <Link
          to={`/expenses/${card.id}`}
          state={linkState}
          className="kanban-card-name"
          title="Unklare Seite ansehen vor Klassifizierung"
        >
          {card.pdf_orig_filename}
        </Link>
      </div>
      <div className="kanban-card-meta">Seite {(card as any).source_page_index}</div>
      <div className="unknown-actions">
        {/* ANLASSFALL 2026-08-12 (Franka, 3G): hier standen VIER Knoepfe — zweimal
            „Als Zulagenblatt", zweimal „Als Fahrtenbuch", gleiche Beschriftung,
            unterschiedliches Verhalten. Der eine Weg (kind-Flip) verschob die Karte
            OHNE Typ, der andere (set-doc-type) setzte den Typ OHNE zu verschieben.
            Beide sind halbe Aktionen; der kind-Flip erzeugte vier typlose Karten,
            die die Stundenliste des BESTANDSMANDANTEN rendeten — bei einem
            Mandanten, der auf dieser Route gar keine Stundenliste kennt.
            Jetzt: genau ein Knopf je registrierter Dokumentart des Mandanten, und
            der macht beides — verschieben UND typisieren. Was der Mandant nicht
            kennt, ist nicht waehlbar. Ohne Registry (ungeseedeter Mandant) bleibt
            der kind-Flip als Notweg. */}
        {docTypeOptions.length > 0 ? (
          docTypeOptions.map((d) => (
            <button key={d.id} className="link" disabled={busy}
                    onClick={() => reclassify(d.routesTo, d.id)}>
              Als {d.label}
            </button>
          ))
        ) : (
          <>
            <button className="link" disabled={busy} onClick={() => reclassify("timesheet")}>
              Als Stundenliste
            </button>
            <button className="link" disabled={busy} onClick={() => reclassify("expense")}>
              Als Diät
            </button>
          </>
        )}
        <button className="link" disabled={busy} onClick={archive}>
          Verwerfen
        </button>
      </div>
      {isDeveloper && (
        <Link
          to={werkstattLink(card)}
          state={linkState}
          className="kanban-card-werkstatt-link"
          data-perm="kanban-werkstatt-link"
          title="Pipeline-Werkstatt für diese Karte öffnen — Modal, schließen kehrt zurück (developer-only)"
          style={{ fontSize: 11, color: "#1d4ed8", textDecoration: "none", marginTop: 4 }}
        >
          🔬 Werkstatt →
        </Link>
      )}
    </div>
  );
}

/**
 * Eine Comprehensive-Pille statt zwei separater Aktionen. Mental-Model:
 *   "Monats-CSV holen" = alle exportierbaren UND bereits exportierten Karten,
 *   transitioniert die neuen (calculated) zu exported. One-Click-Lösung für
 *   den 99%-Fall.
 *
 * Der seltene Audit-Modus (nur Re-Export ohne State-Change) lebt unter "..."
 * mit explizitem Hinweis. Phase 6 Werkstatt hat die Advanced-Trennung.
 */
function KanbanExportButtons({
  month, clientId, status, onAfter,
}: {
  month: string;
  clientId: string;
  status: { ready: boolean; total: number; ready_for: number };
  onAfter: () => void;
}) {
  const ready = status.ready_for;
  const skip = status.total - ready;
  const empty = ready === 0;

  async function comprehensive() {
    if (empty) {
      alert("Keine exportierbaren Karten im Monat.");
      return;
    }
    const msg = skip > 0
      ? `Monats-CSV für ${month}\n\n${ready} Karten in CSV (calculated → exported).\n${skip} Karten werden übersprungen (nicht berechnet).\n\nFortfahren?`
      : `Monats-CSV für ${month}\n\n${ready} Karten in CSV.\n\nCalculated → exported, exportierte bleiben exported.`;
    if (!confirm(msg)) return;
    try {
      await api.export.download(month, clientId, { includeExported: true });
      onAfter();
    } catch (err: any) {
      alert(`Export fehlgeschlagen: ${err.message ?? err}`);
    }
  }

  async function audit() {
    if (empty) return;
    if (!confirm(
      `Audit-Re-Export für ${month}\n\n` +
      `${ready} Karten in CSV — KEIN State-Change.\n` +
      `Für QA / Korrektur-Iteration, ohne den Monats-Workflow zu beeinflussen.`,
    )) return;
    try {
      await api.export.download(month, clientId, { includeExported: true, auditOnly: true });
    } catch (err: any) {
      alert(`Audit-Re-Export fehlgeschlagen: ${err.message ?? err}`);
    }
  }

  // Monats-Neuberechnung (2026-06-10, Thomas — Cross-Month): wenn der
  // VORMONAT nachträglich fertig kontrolliert wurde (z.B. März nach April),
  // stimmen die Mehr-/Überstunden der ersten geteilten Woche dieses Monats
  // nicht mehr — die Regel zieht Vormonats-Tage in die Wochen-Bewertung.
  // Requeued alle berechneten + exportierten Karten; danach Monats-CSV neu.
  async function recalc() {
    if (empty) return;
    if (!confirm(
      `Monat ${month} neu berechnen?\n\n` +
      `Alle ${ready} berechneten + exportierten Karten werden mit dem ` +
      `AKTUELLEN Vormonats-Stand neu gerechnet (geteilte-Woche-Regel).\n` +
      `Exportierte Karten wandern zurück auf „Berechnet" — danach das ` +
      `Monats-CSV neu ziehen.\n\nKorrekturen und erfasste Werte bleiben erhalten.`,
    )) return;
    try {
      const r = await api.export.recalculate(month, clientId);
      alert(
        `Neuberechnung gestartet: ${r.requeued_timesheets} Stundenlisten + ` +
        `${r.requeued_expenses} Diäten. Ergebnis erscheint in ~1 Minute in ` +
        `der Spalte „Berechnet" — dann Monats-CSV neu ziehen.`,
      );
      onAfter();
    } catch (err: any) {
      alert(`Neuberechnung fehlgeschlagen: ${err.message ?? err}`);
    }
  }

  return (
    <>
      <a
        href="#"
        onClick={(e) => { e.preventDefault(); comprehensive(); }}
        className={`upload-button${empty ? " disabled" : ""}`}
        style={{
          cursor: empty ? "not-allowed" : "pointer",
          opacity: empty ? 0.5 : 1,
        }}
        title={
          empty
            ? "Keine exportierbaren Karten im Monat"
            : skip > 0
              ? `Monats-CSV: ${ready} Karten exportierbar, ${skip} werden übersprungen (nicht berechnet)`
              : `Monats-CSV: ${ready} Karten · calculated wird auf exported gesetzt`
        }
      >
        📥 Monats-CSV ({ready}/{status.total})
      </a>
      {!empty && (
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); audit(); }}
          className="upload-button"
          style={{
            cursor: "pointer",
            background: "transparent",
            color: "#1d4ed8",
            border: "1px solid #93c5fd",
            fontSize: 12,
            opacity: 0.85,
          }}
          title="Audit-Re-Export — gleicher CSV-Inhalt, KEIN State-Change (für QA/Korrektur)"
        >
          🔍 Audit-Kopie
        </a>
      )}
      {!empty && (
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); recalc(); }}
          className="upload-button"
          style={{
            cursor: "pointer",
            background: "transparent",
            color: "#b45309",
            border: "1px solid #fcd34d",
            fontSize: 12,
            opacity: 0.9,
          }}
          title={`Alle berechneten + exportierten Karten des Monats neu berechnen — nötig, wenn der Vormonat nachträglich fertig wurde (geteilte-Woche-Regel zieht Vormonats-Tage in die erste Wochen-Bewertung). Danach Monats-CSV neu ziehen.`}
        >
          ↻ Neu berechnen
        </a>
      )}
    </>
  );
}
